'use client'

import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'

import { EXPORT_MENU_ITEMS } from '@/constants/exportMenu'
import GitHub from '../Icons/GitHub'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

export function CopyButton({ sourceCode }) {
  const { copy } = useCopyToClipboard()

  const [pageUrl, setPageUrl] = useState('')
  const [pathname, setPathname] = useState('')

  useEffect(() => {
    setPageUrl(location.href)
    setPathname(window.location.pathname.replace(/\/$/, ''))
  }, [])

  const handleCopy = useCallback(async () => {
    await copy(sourceCode)
    const { toast } = await import('sonner')
    toast.success('Copied to clipboard')
  }, [sourceCode, copy])

  const handleViewMarkdown = useCallback(() => {
    const blob = new Blob([sourceCode || ''], { type: 'text/plain' })
    window.open(URL.createObjectURL(blob), '_blank')
  }, [sourceCode])

  const handleChatGPT = useCallback(() => {
    const q = `Read from ${pageUrl} so I can ask questions about it.`
    window.open(`https://chatgpt.com/?prompt=${encodeURIComponent(q)}`)
  }, [pageUrl])

  const handleClaude = useCallback(() => {
    const q = `Read from ${pageUrl} so I can ask questions about it.`
    window.open(`https://claude.ai/new?q=${encodeURIComponent(q)}`)
  }, [pageUrl])

  const handleVSCode = useCallback(() => {
    window.open(`vscode:extension/anthropic.claude-code`)
  }, [])

  const handlePDF = useCallback(() => {
    window.print()
  }, [])

  const handleEdit = useCallback(() => {
    window.open(`https://github.com/Entropy-Foundation/supra-documentation/tree/master/content${pathname}.mdx`)
  }, [pathname])

  const actionMap = {
    copy: handleCopy,
    markdown: handleViewMarkdown,
    chatgpt: handleChatGPT,
    claude: handleClaude,
    vscode: handleVSCode,
    pdf: handlePDF,
    edit: handleEdit,
  }

  return (
    <div className="absolute ml-auto sm:relative top-2 sm:top-8 w-fit! right-2">
      <DropdownMenu>
        <div className="inline-flex border rounded-md overflow-hidden">
          <button
            type="button"
            onClick={handleEdit}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
          >
            <GitHub className="size-4" />
            Edit
          </button>
          <div className="w-px bg-gray-200 dark:bg-neutral-800" />
          <DropdownMenuTrigger asChild className="focus:outline-none focus:ring-offset-0 group">
            <button
              type="button"
              aria-label="More page actions"
              className="flex items-center px-1.5 py-1 text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
            >
              <ChevronDown className="size-3.5 group-data-[state=open]:rotate-180 transition-all ease-in-out duration-200 rotate-0" />
            </button>
          </DropdownMenuTrigger>
        </div>

        <DropdownMenuContent align="end" className="w-72">
          {EXPORT_MENU_ITEMS.map((group, i) => (
            <Fragment key={group.group}>
              <DropdownMenuGroup className="space-y-1 p-1">
                {group.items.map(({ icon: Icon, label, description, action, external }) => (
                  <DropdownMenuItem
                    key={label}
                    onClick={actionMap[action]}
                    className={`flex items-baseline gap-3.5 group`}
                  >
                    <Icon className="size-4 relative top-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1.5 text-sm font-medium">
                        {label}
                        {external && <ExternalLink className="text-muted-foreground size-3" />}
                      </div>
                      <p className={`text-xs text-muted-foreground/70 group-hover:text-muted-foreground/70!`}>
                        {description}
                      </p>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
              {i !== EXPORT_MENU_ITEMS.length - 1 && <DropdownMenuSeparator />}
            </Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
